import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
    HiAcademicCap,
    HiArrowRight,
    HiBriefcase,
    HiCheckBadge,
    HiBuildingOffice2,
} from "react-icons/hi2";

import placedStudents from "../data/placedStudents";

const PlacementSuccessSection = () => {
    const publishedStudents = placedStudents.filter(
        (student) => student.published
    );

    const [activeId, setActiveId] = useState(
        publishedStudents.find((student) => student.featured)?.id ||
        publishedStudents[0]?.id
    );

    const activeStudent = publishedStudents.find(
        (student) => student.id === activeId
    );

    if (!activeStudent) {
        return null;
    }

    return (
        <section
            aria-labelledby="placement-success-heading"
            className="
        relative
        overflow-hidden
        bg-[#fafbff]
        py-12
        sm:py-14
        lg:py-16
        dark:bg-gray-950
      "
        >
            {/* =========================================================
          BACKGROUND
      ========================================================== */}

            <div
                aria-hidden="true"
                className="
          pointer-events-none
          absolute
          left-1/2
          top-[-180px]
          h-[380px]
          w-[700px]
          -translate-x-1/2
          rounded-full
          bg-indigo-500/[0.035]
          blur-[110px]
          dark:bg-indigo-500/[0.06]
        "
            />

            <div
                aria-hidden="true"
                className="
          pointer-events-none
          absolute
          bottom-[-140px]
          right-[-100px]
          h-[300px]
          w-[300px]
          rounded-full
          bg-cyan-400/[0.025]
          blur-[90px]
        "
            />

            {/* =========================================================
          CONTAINER
      ========================================================== */}

            <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                {/* =======================================================
    HEADER
======================================================== */}

                <div
                    className="
    flex
    flex-col
    items-center
    text-center
  "
                >
                    <div className="flex max-w-2xl flex-col items-center">

                        {/* Eyebrow */}

                        <div
                            className="
        inline-flex
        items-center
        gap-2
        rounded-full
        border
        border-indigo-100
        bg-indigo-50
        px-3
        py-1
        text-[9px]
        font-bold
        uppercase
        tracking-[0.15em]
        text-indigo-600
        dark:border-indigo-900
        dark:bg-indigo-950/40
        dark:text-indigo-300
      "
                        >
                            <span
                                className="
          h-1.5
          w-1.5
          rounded-full
          bg-indigo-500
        "
                            />

                            Placement Success
                        </div>

                        {/* Heading */}

                        <h2
                            id="placement-success-heading"
                            className="
        mt-4
        text-[1.8rem]
        font-extrabold
        leading-[1.1]
        tracking-[-0.04em]
        text-gray-950
        sm:text-[2.15rem]
        lg:text-[2.45rem]
        dark:text-white
      "
                        >
                            From Learning to{" "}
                            <span
                                className="
          bg-gradient-to-r
          from-indigo-600
          via-blue-600
          to-cyan-500
          bg-clip-text
          text-transparent
        "
                            >
                                Career Success
                            </span>
                        </h2>

                        {/* Description */}

                        <p
                            className="
        mt-3
        max-w-xl
        text-xs
        leading-6
        text-gray-600
        sm:text-sm
        dark:text-gray-300
      "
                        >
                            Explore placement journeys of IA4Solutions students who turned
                            their skills into career opportunities.
                        </p>

                    </div>
                </div>

                {/* =======================================================
            SPOTLIGHT CARD
        ======================================================== */}

                <div
                    className="
            mt-8
            overflow-hidden
            rounded-[24px]
            border
            border-gray-200/80
            bg-white
            shadow-[0_18px_55px_rgba(15,23,42,0.07)]
            dark:border-gray-800
            dark:bg-gray-900
            dark:shadow-[0_20px_55px_rgba(0,0,0,0.25)]
          "
                >
                    <div
                        className="
              grid
              lg:grid-cols-[1fr_0.95fr]
            "
                    >

                        {/* ===================================================
                IMAGE
            ==================================================== */}

                        <div
                            className="
                relative
                min-h-[330px]
                overflow-hidden
                border-b
                border-gray-200
                bg-gray-100
                sm:min-h-[390px]
                lg:min-h-[430px]
                lg:border-b-0
                lg:border-r
                dark:border-gray-800
                dark:bg-gray-950
              "
                        >
                            {/* Blurred backdrop */}

                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={`${activeStudent.id}-bg`}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                    className="absolute inset-0"
                                >
                                    <img
                                        src={activeStudent.image}
                                        alt=""
                                        aria-hidden="true"
                                        className="
                      h-full
                      w-full
                      scale-110
                      object-cover
                      opacity-25
                      blur-3xl
                    "
                                    />

                                    <div
                                        className="
                      absolute
                      inset-0
                      bg-white/70
                      dark:bg-gray-950/75
                    "
                                    />
                                </motion.div>
                            </AnimatePresence>

                            {/* Image frame */}

                            <div
                                className="
                  absolute
                  inset-4
                  rounded-[20px]
                  border
                  border-white/70
                  bg-white/20
                  shadow-inner
                  backdrop-blur-sm
                  sm:inset-5
                  dark:border-white/10
                  dark:bg-white/[0.03]
                "
                            />

                            {/* Actual student image */}

                            <div
                                className="
                  absolute
                  inset-5
                  flex
                  items-center
                  justify-center
                  overflow-hidden
                  rounded-[18px]
                  sm:inset-6
                "
                            >
                                <AnimatePresence mode="wait">
                                    <motion.img
                                        key={activeStudent.id}
                                        src={activeStudent.image}
                                        alt={activeStudent.name}
                                        initial={{
                                            opacity: 0,
                                            scale: 0.98,
                                        }}
                                        animate={{
                                            opacity: 1,
                                            scale: 1,
                                        }}
                                        exit={{
                                            opacity: 0,
                                            scale: 0.98,
                                        }}
                                        transition={{
                                            duration: 0.35,
                                        }}
                                        className="
                      relative
                      z-10
                      max-h-full
                      max-w-full
                      object-contain
                      drop-shadow-[0_16px_28px_rgba(15,23,42,0.18)]
                    "
                                    />
                                </AnimatePresence>
                            </div>

                            {/* Spotlight tag */}

                            <div
                                className="
                  absolute
                  left-6
                  top-6
                  z-20
                  inline-flex
                  items-center
                  gap-1.5
                  rounded-full
                  bg-gray-950/45
                  px-2.5
                  py-1.5
                  text-[8px]
                  font-bold
                  uppercase
                  tracking-[0.15em]
                  text-white
                  backdrop-blur-md
                "
                            >
                                <span
                                    className="
                    h-1.5
                    w-1.5
                    rounded-full
                    bg-emerald-400
                  "
                                />

                                Placement Spotlight
                            </div>

                            {/* Student identity */}

                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={`${activeStudent.id}-identity`}
                                    initial={{
                                        opacity: 0,
                                        y: 8,
                                    }}
                                    animate={{
                                        opacity: 1,
                                        y: 0,
                                    }}
                                    exit={{
                                        opacity: 0,
                                        y: -5,
                                    }}
                                    transition={{
                                        duration: 0.3,
                                    }}
                                    className="
                    absolute
                    inset-x-0
                    bottom-0
                    z-20
                    p-5
                    sm:p-6
                  "
                                >
                                    <div
                                        className="
                      rounded-xl
                      border
                      border-white/10
                      bg-gray-950/60
                      px-4
                      py-3
                      backdrop-blur-lg
                    "
                                    >
                                        <p
                                            className="
                        text-[8px]
                        font-bold
                        uppercase
                        tracking-[0.15em]
                        text-cyan-300
                      "
                                        >
                                            {activeStudent.course}
                                        </p>

                                        <h3
                                            className="
                        mt-1
                        text-xl
                        font-extrabold
                        tracking-[-0.03em]
                        text-white
                        sm:text-2xl
                      "
                                        >
                                            {activeStudent.name}
                                        </h3>

                                        {activeStudent.role && (
                                            <p
                                                className="
                          mt-1
                          text-[10px]
                          text-white/65
                        "
                                            >
                                                {activeStudent.role}
                                            </p>
                                        )}
                                    </div>
                                </motion.div>
                            </AnimatePresence>
                        </div>

                        {/* ===================================================
                DETAILS
            ==================================================== */}

                        <div
                            className="
                flex
                flex-col
                justify-center
                p-5
                sm:p-7
                lg:p-8
                xl:p-10
              "
                        >
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={activeStudent.id}
                                    initial={{
                                        opacity: 0,
                                        y: 8,
                                    }}
                                    animate={{
                                        opacity: 1,
                                        y: 0,
                                    }}
                                    exit={{
                                        opacity: 0,
                                        y: -6,
                                    }}
                                    transition={{
                                        duration: 0.3,
                                    }}
                                >

                                    {/* Status row */}

                                    <div
                                        className="
                      flex
                      flex-wrap
                      items-center
                      justify-between
                      gap-2
                    "
                                    >
                                        <div
                                            className="
                        inline-flex
                        items-center
                        gap-1.5
                        rounded-full
                        border
                        border-emerald-100
                        bg-emerald-50
                        px-2.5
                        py-1
                        text-[8px]
                        font-bold
                        uppercase
                        tracking-[0.12em]
                        text-emerald-700
                        dark:border-emerald-900
                        dark:bg-emerald-950/40
                        dark:text-emerald-400
                      "
                                        >
                                            <HiCheckBadge className="text-xs" />
                                            Career Outcome
                                        </div>

                                        <span
                                            className="
                        rounded-full
                        border
                        border-gray-200
                        bg-gray-50
                        px-2.5
                        py-1
                        text-[8px]
                        font-semibold
                        text-gray-500
                        dark:border-gray-800
                        dark:bg-gray-800
                        dark:text-gray-400
                      "
                                        >
                                            {activeStudent.course}
                                        </span>
                                    </div>

                                    {/* Company */}

                                    <div className="mt-7">
                                        <p
                                            className="
                        text-[9px]
                        font-bold
                        uppercase
                        tracking-[0.14em]
                        text-gray-400
                      "
                                        >
                                            Selected at
                                        </p>

                                        <div
                                            className="
                        mt-3
                        flex
                        items-center
                        gap-3
                      "
                                        >
                                            <div
                                                className="
                          flex
                          h-14
                          w-14
                          shrink-0
                          items-center
                          justify-center
                          rounded-xl
                          border
                          border-gray-200
                          bg-white
                          p-2.5
                          shadow-sm
                          dark:border-gray-800
                          dark:bg-gray-800
                        "
                                            >
                                                {activeStudent.companyLogo ? (
                                                    <img
                                                        src={activeStudent.companyLogo}
                                                        alt={activeStudent.company}
                                                        className="
                              max-h-full
                              max-w-full
                              object-contain
                            "
                                                    />
                                                ) : (
                                                    <div
                                                        className="
                              flex
                              h-full
                              w-full
                              items-center
                              justify-center
                              rounded-lg
                              bg-indigo-50
                              text-xs
                              font-extrabold
                              text-indigo-600
                              dark:bg-indigo-950/50
                              dark:text-indigo-300
                            "
                                                    >
                                                        {activeStudent.company?.slice(0, 2)}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="min-w-0">
                                                <div
                                                    className="
                            flex
                            items-center
                            gap-1.5
                            text-[9px]
                            font-semibold
                            text-gray-400
                          "
                                                >
                                                    <HiBuildingOffice2 className="text-xs" />
                                                    Company
                                                </div>

                                                <h3
                                                    className="
                            mt-0.5
                            text-xl
                            font-extrabold
                            leading-tight
                            tracking-[-0.03em]
                            text-gray-950
                            sm:text-2xl
                            dark:text-white
                          "
                                                >
                                                    {activeStudent.company}
                                                </h3>

                                                {activeStudent.role && (
                                                    <p
                                                        className="
                              mt-0.5
                              text-[10px]
                              text-gray-500
                              dark:text-gray-400
                            "
                                                    >
                                                        {activeStudent.role}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Divider */}

                                    <div
                                        className="
                      my-6
                      h-px
                      bg-gray-100
                      dark:bg-gray-800
                    "
                                    />

                                    {/* Package */}

                                    <div
                                        className="
                      relative
                      overflow-hidden
                      rounded-[18px]
                      border
                      border-indigo-100
                      bg-gradient-to-br
                      from-indigo-50
                      via-white
                      to-cyan-50
                      p-4
                      sm:p-5
                      dark:border-indigo-900/50
                      dark:from-indigo-950/50
                      dark:via-gray-900
                      dark:to-cyan-950/30
                    "
                                    >
                                        <div
                                            aria-hidden="true"
                                            className="
                        pointer-events-none
                        absolute
                        right-[-40px]
                        top-[-40px]
                        h-[120px]
                        w-[120px]
                        rounded-full
                        bg-indigo-500/10
                        blur-[40px]
                      "
                                        />

                                        <div
                                            className="
                        relative
                        flex
                        items-center
                        justify-between
                        gap-4
                      "
                                        >
                                            <div>
                                                <p
                                                    className="
                            text-[9px]
                            font-bold
                            uppercase
                            tracking-[0.14em]
                            text-indigo-500
                            dark:text-indigo-300
                          "
                                                >
                                                    Annual Package
                                                </p>

                                                <p
                                                    className="
                            mt-1
                            text-2xl
                            font-extrabold
                            tracking-[-0.04em]
                            text-gray-950
                            sm:text-3xl
                            dark:text-white
                          "
                                                >
                                                    {activeStudent.package}
                                                </p>

                                                <p
                                                    className="
                            mt-1
                            text-[9px]
                            text-gray-500
                            dark:text-gray-400
                          "
                                                >
                                                    Career opportunity achieved
                                                </p>
                                            </div>

                                            <div
                                                className="
                          flex
                          h-10
                          w-10
                          shrink-0
                          items-center
                          justify-center
                          rounded-xl
                          bg-white
                          text-indigo-600
                          shadow-sm
                          dark:bg-gray-800
                          dark:text-indigo-400
                        "
                                            >
                                                <HiBriefcase className="text-base" />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Course */}

                                    <div
                                        className="
                      mt-4
                      flex
                      items-center
                      gap-2.5
                      rounded-xl
                      border
                      border-gray-100
                      bg-gray-50
                      px-3
                      py-2.5
                      dark:border-gray-800
                      dark:bg-gray-800/50
                    "
                                    >
                                        <div
                                            className="
                        flex
                        h-7
                        w-7
                        shrink-0
                        items-center
                        justify-center
                        rounded-lg
                        bg-indigo-100
                        text-indigo-600
                        dark:bg-indigo-950/60
                        dark:text-indigo-400
                      "
                                        >
                                            <HiAcademicCap className="text-sm" />
                                        </div>

                                        <p
                                            className="
                        text-[10px]
                        leading-4
                        text-gray-600
                        dark:text-gray-300
                      "
                                        >
                                            Trained through{" "}
                                            <span className="font-bold text-gray-900 dark:text-white">
                                                {activeStudent.course}
                                            </span>
                                        </p>
                                    </div>
                                </motion.div>
                            </AnimatePresence>
                        </div>
                    </div>
                </div>

                {/* =======================================================
            STUDENT SELECTOR
        ======================================================== */}

                <div className="mt-7 sm:mt-8">

                    <div
                        className="
              mb-3
              flex
              items-center
              justify-between
              gap-3
            "
                    >
                        <div>
                            <p
                                className="
                  text-[9px]
                  font-bold
                  uppercase
                  tracking-[0.15em]
                  text-gray-400
                "
                            >
                                Placement Stories
                            </p>

                            <p
                                className="
                  mt-0.5
                  text-[11px]
                  text-gray-500
                  dark:text-gray-400
                "
                            >
                                Explore student outcomes
                            </p>
                        </div>

                        <p
                            className="
                hidden
                text-[9px]
                text-gray-400
                sm:block
              "
                        >
                            Select a profile
                        </p>
                    </div>

                    <div
                        className="
              flex
              gap-2.5
              overflow-x-auto
              pb-2
              scrollbar-thin
              scrollbar-track-transparent
              scrollbar-thumb-gray-300
              dark:scrollbar-thumb-gray-700
            "
                    >
                        {publishedStudents.map((student) => {
                            const isActive = student.id === activeStudent.id;

                            return (
                                <button
                                    key={student.id}
                                    type="button"
                                    onClick={() => setActiveId(student.id)}
                                    aria-pressed={isActive}
                                    className={`
                    group
                    relative
                    flex
                    min-w-[175px]
                    items-center
                    gap-2.5
                    overflow-hidden
                    rounded-xl
                    border
                    px-2.5
                    py-2.5
                    text-left
                    transition-all
                    duration-300
                    sm:min-w-[195px]
                    ${isActive
                                            ? `
                          border-indigo-200
                          bg-white
                          shadow-[0_8px_22px_rgba(79,70,229,0.08)]
                          dark:border-indigo-800
                          dark:bg-gray-900
                        `
                                            : `
                          border-gray-200
                          bg-white/80
                          hover:border-gray-300
                          hover:bg-white
                          hover:shadow-sm
                          dark:border-gray-800
                          dark:bg-gray-900/70
                        `
                                        }
                  `}
                                >
                                    {isActive && (
                                        <span
                                            className="
                        absolute
                        bottom-0
                        left-0
                        top-0
                        w-0.5
                        bg-gradient-to-b
                        from-indigo-500
                        to-cyan-500
                      "
                                        />
                                    )}

                                    <div
                                        className="
                      h-11
                      w-11
                      shrink-0
                      overflow-hidden
                      rounded-lg
                      bg-gray-100
                      ring-1
                      ring-gray-200
                      dark:bg-gray-800
                      dark:ring-gray-700
                    "
                                    >
                                        <img
                                            src={student.image}
                                            alt={student.name}
                                            className="
                        h-full
                        w-full
                        object-cover
                        transition-transform
                        duration-300
                        group-hover:scale-105
                      "
                                        />
                                    </div>

                                    <div className="min-w-0 flex-1">
                                        <p
                                            className={`
                        truncate
                        text-[10px]
                        font-bold
                        ${isActive
                                                    ? "text-indigo-700 dark:text-indigo-300"
                                                    : "text-gray-800 dark:text-gray-200"
                                                }
                      `}
                                        >
                                            {student.name}
                                        </p>

                                        <p
                                            className="
                        mt-0.5
                        truncate
                        text-[9px]
                        text-gray-400
                      "
                                        >
                                            {student.company}
                                        </p>

                                        <p
                                            className="
                        mt-0.5
                        text-[9px]
                        font-bold
                        text-gray-500
                        dark:text-gray-400
                      "
                                        >
                                            {student.package}
                                        </p>
                                    </div>

                                    <HiArrowRight
                                        className={`
                      shrink-0
                      text-xs
                      transition-all
                      duration-300
                      ${isActive
                                                ? "text-indigo-500"
                                                : "text-gray-300 opacity-0 group-hover:opacity-100 dark:text-gray-600"
                                            }
                    `}
                                    />
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* =======================================================
            BOTTOM CTA
        ======================================================== */}

                <div className="mt-7 flex justify-center sm:mt-8">
                    <Link
                        to="/placements"
                        className="
              group
              inline-flex
              items-center
              gap-2
              rounded-full
              bg-gray-950
              px-5
              py-2.5
              text-[11px]
              font-bold
              text-white
              shadow-[0_8px_24px_rgba(15,23,42,0.12)]
              transition-all
              duration-300
              hover:-translate-y-0.5
              hover:bg-indigo-600
              dark:bg-white
              dark:text-gray-950
              dark:hover:bg-indigo-500
              dark:hover:text-white
            "
                    >
                        Explore all placements

                        <HiArrowRight
                            className="
                text-sm
                transition-transform
                duration-300
                group-hover:translate-x-1
              "
                        />
                    </Link>
                </div>
            </div>
        </section>
    );
};

export default PlacementSuccessSection;